import { useEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authClient } from '../auth'
import { queryClient } from '../lib/queryClient'
import {
  clearPersistedPortalContext,
  usePortalContext,
  type PortalContext,
} from '../lib/portalContext'
import { setSelectedPortalContext } from '../lib/organizationSelection'
import { switchPortalAccount } from '../lib/portalNavigation'

export type NavItem = { to: string; labelKey: string }
export type NavGroup = { groupKey: string; items: readonly NavItem[] }

function NavItems({
  groups,
  onNavigate,
}: {
  groups: readonly NavGroup[]
  onNavigate?: () => void
}) {
  const { t } = useTranslation()
  return (
    <ul className="space-y-4">
      {groups.map((group) => (
        <li key={group.groupKey}>
          <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t(group.groupKey)}
          </p>
          <ul className="space-y-1">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-2 text-sm ${
                      isActive ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

// Presentational shell shared by both portal identities (creator and
// org-admin). The caller supplies the nav groups for its identity; session,
// sign-out and the impersonation banner are identity-agnostic.
export function AppShell({
  navGroups,
  children,
}: {
  navGroups: readonly NavGroup[]
  children: ReactNode
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data } = authClient.useSession()
  const portal = usePortalContext()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)
  const accountTriggerRef = useRef<HTMLButtonElement>(null)

  // Set by the better-auth admin plugin when a Bocify admin is impersonating the
  // current user. Surfaced as a banner with a one-click exit.
  const impersonatedBy = (data?.session as { impersonatedBy?: string | null } | undefined)
    ?.impersonatedBy

  useEffect(() => {
    if (!accountOpen) return
    function closeOnOutsideClick(event: MouseEvent) {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false)
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setAccountOpen(false)
      accountTriggerRef.current?.focus()
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [accountOpen])

  async function signOut() {
    // navigate() below is SPA navigation (no reload), so the in-memory tenant
    // singleton must be reset too — otherwise the next user signing in from
    // this tab would inherit the previous user's organization id.
    if (portal.userId) clearPersistedPortalContext(portal.userId)
    setSelectedPortalContext(undefined)
    await authClient.signOut()
    queryClient.clear()
    navigate('/signin', { replace: true })
  }

  function switchAccount(context: PortalContext) {
    if (!portal.userId) return
    switchPortalAccount(portal.userId, context, (url) => window.location.assign(url))
  }

  async function stopImpersonating() {
    await authClient.admin.stopImpersonating()
    queryClient.clear()
    // Hard reload so every query re-resolves as the admin's own (empty) context
    // rather than the impersonated user's cached data.
    window.location.reload()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {impersonatedBy && (
        <div className="flex items-center justify-center gap-3 bg-amber-400 px-4 py-2 text-center text-sm font-medium text-amber-950">
          <span>{t('impersonation.banner', { email: data?.user?.email ?? '' })}</span>
          <button
            onClick={stopImpersonating}
            className="rounded-md border border-amber-700/40 bg-amber-300 px-2 py-0.5 text-xs font-semibold hover:bg-amber-200"
          >
            {t('impersonation.stop')}
          </button>
        </div>
      )}
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              aria-label={t('shell.toggleNav')}
            >
              {t('shell.menu')}
            </button>
            <h1 className="text-base font-semibold text-gray-900">{t('app.name')}</h1>
          </div>
          <div ref={accountRef} className="relative">
            <button
              ref={accountTriggerRef}
              type="button"
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              aria-controls="account-menu"
              onClick={() => setAccountOpen((open) => !open)}
              className="max-w-[16rem] truncate rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {data?.user?.email}
            </button>
            {accountOpen && (
              <div
                id="account-menu"
                role="menu"
                className="absolute right-0 z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              >
                {portal.data?.response.contexts.map((context) => {
                  const isCurrent =
                    portal.data?.selected.kind === context.kind &&
                    portal.data.selected.id === context.id
                  return (
                    <button
                      key={`${context.kind}:${context.id}`}
                      type="button"
                      role="menuitem"
                      onClick={() => (isCurrent ? setAccountOpen(false) : switchAccount(context))}
                      className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-gray-900">
                          {context.displayName}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {context.kind === 'organization'
                            ? t('shell.accountOrganization')
                            : t('shell.accountCreator')}
                        </span>
                      </span>
                      {isCurrent && (
                        <span className="text-xs text-gray-500">{t('shell.accountCurrent')}</span>
                      )}
                    </button>
                  )
                })}
                <div className="my-1 border-t border-gray-200" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={signOut}
                  className="min-h-12 w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus:bg-gray-50"
                >
                  {t('shell.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
        {mobileOpen && (
          <nav className="md:hidden border-t border-gray-200 bg-white px-4 py-2">
            <NavItems groups={navGroups} onNavigate={() => setMobileOpen(false)} />
          </nav>
        )}
      </header>

      <div className="mx-auto flex max-w-6xl gap-6 px-4 py-6 md:px-6">
        <aside className="hidden md:block w-48 shrink-0">
          <nav className="sticky top-6">
            <NavItems groups={navGroups} />
          </nav>
        </aside>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  )
}
